import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Building2, CheckCircle2, AlertTriangle, Circle, ExternalLink, ArrowUpDown,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import { getApiBase } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import type { DashboardTimeRange } from '../lib/timeRange';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { fmtTs } from '../lib/globalTz';
import { sentinelIncidentNumber } from '../lib/utils';

interface SeverityBreakdown { high: number; medium: number; low: number; informational: number }
interface StatusBreakdown { new: number; active: number; closed: number }

interface CustomerSummary {
  customerId: string;
  name: string;
  color: string;
  status: 'live' | 'error' | 'no_credentials';
  incidents: number;
  highSeverity: number;
  secureScore: number | null;
  activeIncidents: number;
  severityBreakdown: SeverityBreakdown;
  statusBreakdown: StatusBreakdown;
  lastFetched: string;
  error?: string;
  errors?: string[];
}

interface MsspSummaryResponse {
  customers: CustomerSummary[];
  fetchedAt: string;
  total: number;
}

interface CrossCustomerIncident {
  id?: string;
  incidentNumber?: number;
  graphIncidentId?: string;
  title?: string;
  severity?: string;
  status?: string;
  createdTime?: string;
  lastUpdateTime?: string;
  webUrl?: string;
  customerId: string;
  customerName: string;
  customerColor: string;
}

interface MsspIncidentsResponse {
  incidents: CrossCustomerIncident[];
  fetchedAt: string;
  totalCustomers: number;
  totalIncidents: number;
  truncated: boolean;
  errors: Record<string, string>;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#3b82f6',
  informational: '#9ca3af',
};

const STATUS_COLORS: Record<string, string> = {
  new: '#3b82f6',
  active: '#f59e0b',
  closed: '#10b981',
};

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-muted-foreground text-sm">-</span>;
  const color = score >= 80 ? 'text-green-600 dark:text-green-400' : score >= 60 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400';
  return <span className={`text-2xl font-bold ${color}`}>{score}%</span>;
}

function StatusBadge({ status }: { status: CustomerSummary['status'] }) {
  if (status === 'live') return <span className="text-[10px] font-bold bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full">● LIVE</span>;
  if (status === 'no_credentials') return <span className="text-[10px] font-bold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded-full">NO CREDS</span>;
  return <span className="text-[10px] font-bold bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded-full">ERROR</span>;
}

function SeverityDot({ severity }: { severity?: string }) {
  const s = (severity || '').toLowerCase();
  const color = SEVERITY_COLORS[s] ?? '#9ca3af';
  return <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} title={severity || 'unknown'} />;
}

function IncidentStatusPill({ status }: { status?: string }) {
  const s = (status || '').toLowerCase();
  const cls = s === 'new'
    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
    : s === 'active' || s === 'inprogress' || s === 'in_progress'
    ? 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
    : s === 'closed' || s === 'resolved'
    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{status || 'unknown'}</span>;
}

function relativeTime(iso?: string): string {
  if (!iso) return '-';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '-';
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return fmtTs(iso, { dateStyle: 'medium' });
}

type SortKey = 'createdTime' | 'severity' | 'customer' | 'status';
type SortDir = 'asc' | 'desc';

const SEVERITY_RANK: Record<string, number> = { high: 4, medium: 3, low: 2, informational: 1 };

interface MsspOverviewTabProps {
  /** Selected lookback window from the global Header pill (1h/4h/8h/12h/24h/7d/30d/60d/90d/All).
   *  The server accepts hour and day tokens; 'all' means the full supported window. */
  dateFilter?: DashboardTimeRange;
}

export function MsspOverviewTab({ dateFilter = '24h' }: MsspOverviewTabProps = {}) {
  const { customers, isLoading: customersLoading } = useCustomers();
  const [data, setData] = useState<MsspSummaryResponse | null>(null);
  const [incidentsData, setIncidentsData] = useState<MsspIncidentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<string>('');

  // Incidents-table state
  const [sortKey, setSortKey] = useState<SortKey>('createdTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = getApiBase();
      const daysParam = dateFilter === 'all' ? 'all' : String(dateFilter);
      const qs = `?days=${encodeURIComponent(daysParam)}`;
      const [sumResp, incResp] = await Promise.all([
        fetch(`${base}/mssp/summary${qs}`, { credentials: 'include' }),
        fetch(`${base}/mssp/incidents${qs}`, { credentials: 'include' }),
      ]);
      if (!sumResp.ok) throw new Error(`summary HTTP ${sumResp.status}`);
      if (!incResp.ok) throw new Error(`incidents HTTP ${incResp.status}`);
      const summary = await sumResp.json() as MsspSummaryResponse;
      const incidents = await incResp.json() as MsspIncidentsResponse;
      setData(summary);
      setIncidentsData(incidents);
      setLastFetch(new Date().toLocaleTimeString());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  // Two narrow effects so dateFilter changes ALWAYS refetch, while the
  // mount/roster effect still avoids the 0→N hydration double-fire.
  //
  // Effect A: customer roster — fires once after CustomerContext finishes
  // loading, then again only when the roster size genuinely changes (e.g.
  // a customer is added/removed in Settings).
  const customerCount = customers.length;
  const prevCustomerCount = useRef<number | null>(null);
  useEffect(() => {
    if (customersLoading) return;
    if (prevCustomerCount.current === customerCount) return;
    prevCustomerCount.current = customerCount;
    fetchAll();
  }, [customerCount, customersLoading, fetchAll]);

  // Effect B: time-range pill — fires whenever dateFilter changes (after the
  // very first ready render, which Effect A already covers). Skipping the
  // initial render here keeps the initial fetch count at exactly one.
  const initialDateFilterApplied = useRef(false);
  useEffect(() => {
    if (customersLoading) return;
    if (!initialDateFilterApplied.current) {
      initialDateFilterApplied.current = true;
      return;
    }
    fetchAll();
  }, [dateFilter, customersLoading, fetchAll]);

  const totals = data?.customers.reduce((acc, c) => ({
    incidents: acc.incidents + (c.incidents ?? 0),
    high: acc.high + (c.highSeverity ?? 0),
    active: acc.active + (c.activeIncidents ?? 0),
    live: acc.live + (c.status === 'live' ? 1 : 0),
  }), { incidents: 0, high: 0, active: 0, live: 0 });

  // ── Chart data ──────────────────────────────────────────────────────────────
  // Stacked bar: one bar per customer, segments = severity buckets.
  const severityChartData = useMemo(() => {
    if (!data) return [];
    return data.customers
      .filter((c) => c.status === 'live')
      .map((c) => ({
        name: c.name,
        High: c.severityBreakdown?.high ?? 0,
        Medium: c.severityBreakdown?.medium ?? 0,
        Low: c.severityBreakdown?.low ?? 0,
        Informational: c.severityBreakdown?.informational ?? 0,
      }));
  }, [data]);

  // Pie: status mix across all customers combined.
  const statusPieData = useMemo(() => {
    if (!data) return [];
    const agg = { new: 0, active: 0, closed: 0 };
    for (const c of data.customers) {
      if (c.status !== 'live') continue;
      agg.new += c.statusBreakdown?.new ?? 0;
      agg.active += c.statusBreakdown?.active ?? 0;
      agg.closed += c.statusBreakdown?.closed ?? 0;
    }
    const out: { key: keyof typeof agg; name: string; value: number }[] = [
      { key: 'new', name: 'New', value: agg.new },
      { key: 'active', name: 'Active', value: agg.active },
      { key: 'closed', name: 'Closed', value: agg.closed },
    ];
    return out.filter((s) => s.value > 0);
  }, [data]);

  // ── Filtered + sorted incidents ────────────────────────────────────────────
  const filteredSortedIncidents = useMemo(() => {
    const list = (incidentsData?.incidents ?? []).filter((i) => {
      if (severityFilter !== 'all' && (i.severity || '').toLowerCase() !== severityFilter) return false;
      if (customerFilter !== 'all' && i.customerId !== customerFilter) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'severity': {
          const ar = SEVERITY_RANK[(a.severity || '').toLowerCase()] ?? 0;
          const br = SEVERITY_RANK[(b.severity || '').toLowerCase()] ?? 0;
          return (ar - br) * dir;
        }
        case 'customer':
          return a.customerName.localeCompare(b.customerName) * dir;
        case 'status':
          return (a.status || '').localeCompare(b.status || '') * dir;
        case 'createdTime':
        default: {
          const at = new Date(a.createdTime || 0).getTime();
          const bt = new Date(b.createdTime || 0).getTime();
          return (at - bt) * dir;
        }
      }
    });
    return list;
  }, [incidentsData, severityFilter, customerFilter, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdTime' ? 'desc' : 'asc');
    }
  }

  const msspIncidentColumns: FilterableColumnDef<CrossCustomerIncident>[] = [
    {
      key: 'severityDot',
      header: '',
      value: (inc) => inc.severity || '',
      render: (inc) => <SeverityDot severity={inc.severity} />,
      headerClassName: 'w-8',
      cellClassName: 'w-8',
    },
    {
      key: 'severity',
      header: 'Severity',
      value: (inc) => inc.severity || '',
      onSort: () => toggleSort('severity'),
      sortLabel: <span className="inline-flex items-center gap-1">Severity <ArrowUpDown size={11} /></span>,
      render: (inc) => <span className="capitalize text-foreground">{inc.severity || '-'}</span>,
    },
    {
      key: 'customer',
      header: 'Customer',
      value: (inc) => inc.customerName,
      onSort: () => toggleSort('customer'),
      sortLabel: <span className="inline-flex items-center gap-1">Customer <ArrowUpDown size={11} /></span>,
      render: (inc) => (
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: inc.customerColor }} />
          <span className="truncate max-w-[140px]" title={inc.customerName}>{inc.customerName}</span>
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      value: (inc) => inc.title || `Incident #${sentinelIncidentNumber(inc) || '-'}`,
      cellClassName: 'text-foreground max-w-[420px]',
      render: (inc) => <span className="block truncate" title={inc.title}>{inc.title || `Incident #${sentinelIncidentNumber(inc) || '-'}`}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      value: (inc) => inc.status || '',
      onSort: () => toggleSort('status'),
      sortLabel: <span className="inline-flex items-center gap-1">Status <ArrowUpDown size={11} /></span>,
      render: (inc) => <IncidentStatusPill status={inc.status} />,
    },
    {
      key: 'createdTime',
      header: 'Created',
      value: (inc) => inc.createdTime || '',
      onSort: () => toggleSort('createdTime'),
      sortLabel: <span className="inline-flex items-center gap-1">Created <ArrowUpDown size={11} /></span>,
      cellClassName: 'text-muted-foreground whitespace-nowrap',
      render: (inc) => <span title={inc.createdTime}>{relativeTime(inc.createdTime)}</span>,
    },
    {
      key: 'open',
      header: '',
      value: (inc) => inc.webUrl || (sentinelIncidentNumber(inc) ? `https://security.microsoft.com/incidents/${sentinelIncidentNumber(inc)}` : ''),
      filterEnabled: false,
      headerClassName: 'w-12',
      cellClassName: 'w-12 text-right',
      render: (inc) => {
        const url = inc.webUrl || (sentinelIncidentNumber(inc) ? `https://security.microsoft.com/incidents/${sentinelIncidentNumber(inc)}` : undefined);
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
            title="Open in Microsoft Defender"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={12} />
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
  ];

  return (
    <div className="slide-in space-y-5">
      {/* Header bar */}
      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              MSSP Customer Overview
              <span className="text-xs font-normal text-muted-foreground">Managed Security Service Provider</span>
            </h2>
            {lastFetch && <p className="text-xs text-muted-foreground mt-0.5">Last refreshed: {lastFetch}</p>}
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity"
          >
            {loading ? 'Fetching…' : 'Refresh All'}
          </button>
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Aggregate KPIs */}
        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[
              { label: 'Total Customers', value: data?.total ?? 0, icon: <Building2 size={16} className="text-blue-600 dark:text-blue-400" />, color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Live Connections', value: totals.live, icon: <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" />, color: 'text-green-600 dark:text-green-400' },
              { label: 'Total Incidents', value: totals.incidents, icon: <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400" />, color: 'text-orange-600 dark:text-orange-400' },
              { label: 'High Severity', value: totals.high, icon: <Circle size={16} className="text-red-600 dark:text-red-400 fill-red-600 dark:fill-red-400" />, color: 'text-red-600 dark:text-red-400' },
            ].map(k => (
              <div key={k.label} className="bg-muted/40 rounded-lg p-3 text-center border border-border">
                <div className="flex justify-center mb-0.5">{k.icon}</div>
                <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
                <div className="text-[10px] text-muted-foreground">{k.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer cards grid */}
      {loading && !data && (
        <div className="text-center py-16 text-muted-foreground">
          <div className="flex justify-center mb-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
          <p>Fetching data from all customers…</p>
          <p className="text-xs mt-1">This may take 15–30 seconds for large portfolios</p>
        </div>
      )}

      {data?.customers && data.customers.length === 0 && (
        <div className="text-center py-16 text-muted-foreground bg-card rounded-xl border border-border">
          <div className="flex justify-center mb-3">
            <Building2 size={32} className="text-muted-foreground/40" />
          </div>
          <p className="font-semibold text-foreground">No customers configured yet</p>
          <p className="text-sm mt-1">Open Settings → Customers to add your managed tenants</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data?.customers.map((c) => (
          <div
            key={c.customerId}
            className="bg-card rounded-xl shadow border border-border overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Color bar */}
            <div className="h-1.5" style={{ backgroundColor: c.color ?? '#6b7280' }} />

            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color ?? '#6b7280' }} />
                  <h3 className="font-semibold text-foreground text-sm truncate max-w-[160px]" title={c.name}>{c.name}</h3>
                </div>
                <StatusBadge status={c.status} />
              </div>

              {c.status === 'no_credentials' ? (
                <div className="text-xs text-muted-foreground italic py-2">
                  No credentials configured. Add them in Settings, Customers.
                </div>
              ) : c.status === 'error' ? (
                <div className="text-xs text-red-600 dark:text-red-400 py-2">
                  {c.error ?? (c.errors?.[0] ?? 'Connection failed')}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-muted/40 rounded-lg p-2 text-center border border-border">
                    <ScoreBadge score={c.secureScore} />
                    <div className="text-[10px] text-muted-foreground mt-0.5">Secure Score</div>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-2 text-center border border-border">
                    <div className={`text-2xl font-bold ${c.incidents > 20 ? 'text-red-600 dark:text-red-400' : c.incidents > 5 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                      {c.incidents}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Incidents</div>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-2 text-center border border-border">
                    <div className={`text-2xl font-bold ${c.highSeverity > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {c.highSeverity}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">High Severity</div>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-2 text-center border border-border">
                    <div className={`text-2xl font-bold ${c.activeIncidents > 5 ? 'text-orange-600 dark:text-orange-400' : 'text-foreground'}`}>
                      {c.activeIncidents}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Active</div>
                  </div>
                </div>
              )}

              {c.lastFetched && (
                <p className="text-[10px] text-muted-foreground mt-2 text-right">
                  {fmtTs(c.lastFetched, { timeStyle: 'short' })}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Charts row ──────────────────────────────────────────────────────── */}
      {data && severityChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-card rounded-xl shadow border border-border p-4 lg:col-span-2">
            <h3 className="text-sm font-bold text-foreground mb-1">Incidents by Severity per Customer</h3>
            <p className="text-xs text-muted-foreground mb-3">Stacked bars show severity mix for each managed tenant</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={severityChartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="High" stackId="sev" fill={SEVERITY_COLORS.high} />
                  <Bar dataKey="Medium" stackId="sev" fill={SEVERITY_COLORS.medium} />
                  <Bar dataKey="Low" stackId="sev" fill={SEVERITY_COLORS.low} />
                  <Bar dataKey="Informational" stackId="sev" fill={SEVERITY_COLORS.informational} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow border border-border p-4">
            <h3 className="text-sm font-bold text-foreground mb-1">Status Mix (All Customers)</h3>
            <p className="text-xs text-muted-foreground mb-3">Combined incident lifecycle distribution</p>
            <div className="h-72">
              {statusPieData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No incident data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      paddingAngle={2}
                      label={(entry) => `${entry.value}`}
                    >
                      {statusPieData.map((entry) => (
                        <Cell key={entry.key} fill={STATUS_COLORS[entry.key]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-customer incidents table ──────────────────────────────────── */}
      {incidentsData && (
        <div className="bg-card rounded-xl shadow border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-foreground">All Customer Incidents</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Showing {filteredSortedIncidents.length} of {incidentsData.totalIncidents} across {incidentsData.totalCustomers} customers
                {incidentsData.truncated && <span className="ml-1 text-orange-600 dark:text-orange-400">(capped at 200)</span>}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="bg-muted/40 border border-border rounded-md text-xs px-2 py-1 text-foreground"
              >
                <option value="all">All severities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="informational">Informational</option>
              </select>
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="bg-muted/40 border border-border rounded-md text-xs px-2 py-1 text-foreground max-w-[180px]"
              >
                <option value="all">All customers</option>
                {data?.customers.map((c) => (
                  <option key={c.customerId} value={c.customerId}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <FilterableColumnTable
            columns={msspIncidentColumns}
            rows={filteredSortedIncidents}
            storageKey="kloudsecure:mssp:all-customer-incidents:columns"
            rowKey={(inc, idx) => `${inc.customerId}-${inc.id ?? inc.incidentNumber ?? idx}`}
            rowClassName={() => 'border-t border-border hover:bg-muted/30'}
            emptyMessage="No incidents match the current filters"
            maxHeightClassName="max-h-[520px]"
            tableClassName="w-full text-xs"
          />
        </div>
      )}
    </div>
  );
}
